import { Popover, InputBase, Checkbox, Button } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';

type Option = string | { value: string; label: string };

type AbbreviationMap = {
  values: Set<string>;
  label: string;
};

// This component has similar behavior to MultiSelect but instead of removable pills, it displays a truncated string of the selected values
// Is compatible with mantine form library
export default function DropdownChecklist({
  darkPlaceholder = true,
  options,
  value = [],
  onChange,
  placeholder,
  label,
  className,
  abbreviations,
  error,
  withApplyButton = false,
  debounceMs,
  ...others
}: {
  darkPlaceholder?: boolean;
  options: Option[];
  value?: string[];
  onChange: (val: string[]) => void;
  placeholder: string;
  label: string;
  className?: string;
  abbreviations?: AbbreviationMap[];
  error?: React.ReactNode;
  withApplyButton?: boolean;
  debounceMs?: number;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}) {
  const [internalValue, setInternalValue] = useState<string[]>(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const [dropdownOpened, setDropdownOpened] = useState(false);
  const closeDropdown = () => {
    setDropdownOpened(false);
    if (withApplyButton) {
      setInternalValue(value);
    }
  };

  const debouncedOnChange = useDebouncedCallback((val: string[]) => {
    onChange(val);
  }, debounceMs || 0);

  const handleSelect = (val: string) => {
    if (!onChange) {
      return;
    }

    if (val === '___clear_all___') {
      if (withApplyButton) {
        setInternalValue([]);
      } else {
        setInternalValue([]);
        if (debounceMs) {
          debouncedOnChange([]);
        } else {
          onChange([]);
          closeDropdown();
        }
      }
      return;
    }

    const currentValue = withApplyButton || debounceMs ? internalValue : value;
    const newValue = currentValue.includes(val) ? currentValue.filter((item) => item !== val) : [...currentValue, val];

    if (withApplyButton || debounceMs) {
      setInternalValue(newValue);
    }

    if (!withApplyButton) {
      if (debounceMs) {
        debouncedOnChange(newValue);
      } else {
        onChange(newValue);
      }
    }
  };

  const handleApply = () => {
    onChange(internalValue);
    closeDropdown();
  };

  const activeValue = withApplyButton || debounceMs ? internalValue : value;

  const dropdownOptions = options.map((option) => {
    const val = typeof option === 'string' ? option : option.value;
    const label = typeof option === 'string' ? option : option.label;

    return (
      <div
        key={val}
        onClick={() => handleSelect(val)}
        className={`flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-stone-50 dark:hover:bg-stone-800 ${activeValue.includes(val) ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' : ''}`}
      >
        <div className="flex items-center gap-2">
          <Checkbox
            checked={activeValue.includes(val)}
            readOnly
            size="xs"
            tabIndex={-1}
            styles={{
              input: { cursor: 'pointer' },
            }}
          />
          <span className={others.size === 'xs' ? 'text-xs' : 'text-sm'}>{label}</span>
        </div>
      </div>
    );
  });

  const getDisplayValue = () => {
    const selectedSet = new Set(value);

    // Find matching abbreviation if it exists
    const matchingAbbreviation = abbreviations?.find((abbreviation: AbbreviationMap) => {
      if (abbreviation.values.size !== selectedSet.size) {
        return false;
      }
      return [...abbreviation.values].every((val) => selectedSet.has(val));
    });

    if (matchingAbbreviation) {
      return matchingAbbreviation.label;
    }

    return value
      .map((val) => {
        const option = options.find((o) => (typeof o === 'string' ? o === val : o.value === val));
        return typeof option === 'string' ? option : (option?.label ?? val);
      })
      .join(', ');
  };

  return (
    <div className={className}>
      <Popover
        opened={dropdownOpened}
        onChange={setDropdownOpened}
        withinPortal={false}
        position="bottom-start"
        shadow="md"
        width="auto" /* Let it grow for long branch names */
      >
        <Popover.Target>
          <InputBase
            component="button"
            type="button"
            onClick={() => setDropdownOpened((o) => !o)}
            label={label}
            pointer
            rightSection={
              <div
                className="flex h-full w-full cursor-pointer items-center justify-end gap-1 pr-1"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDropdownOpened((o) => !o);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                {value.length > 0 && (
                  <div
                    role="button"
                    tabIndex={0}
                    className="flex cursor-pointer items-center justify-center rounded p-0.5 text-stone-400 hover:bg-stone-200 hover:text-stone-600 dark:hover:bg-stone-700 dark:hover:text-stone-300"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleSelect('___clear_all___');
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line
                        x1="18"
                        y1="6"
                        x2="6"
                        y2="18"
                      />
                      <line
                        x1="6"
                        y1="6"
                        x2="18"
                        y2="18"
                      />
                    </svg>
                  </div>
                )}
                <div
                  style={{ transform: dropdownOpened ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
                  className="flex items-center justify-center text-stone-400"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>
            }
            rightSectionPointerEvents="auto"
            rightSectionWidth={value.length > 0 ? 44 : 28}
            error={error}
            {...others}
            classNames={{
              input: `text-ellipsis whitespace-nowrap overflow-hidden ${others.classNames?.input || ''}`,
              ...others.classNames,
            }}
          >
            {value.length > 0 ?
              <span className={`block w-full truncate text-left ${others.size === 'xs' ? 'text-xs' : 'text-sm'}`}>
                {getDisplayValue()}
              </span>
            : <span
                className={`block w-full truncate text-left ${others.size === 'xs' ? 'text-xs' : 'text-sm'} ${darkPlaceholder ? 'font-medium text-stone-900 dark:text-stone-100' : 'text-[var(--mantine-color-placeholder)]'}`}
              >
                {placeholder}
              </span>
            }
          </InputBase>
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {dropdownOptions.length > 0 ?
              dropdownOptions
            : <div className="p-3 text-center text-sm text-stone-500">No options...</div>}
            {activeValue.length > 0 && (
              <div
                onClick={() => handleSelect('___clear_all___')}
                className="cursor-pointer border-t border-stone-100 transition-colors hover:bg-stone-50 dark:border-stone-800 dark:hover:bg-stone-800"
              >
                <div className="flex items-center justify-center py-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 dark:text-stone-400 dark:hover:text-stone-200">
                  Clear all
                </div>
              </div>
            )}
            {withApplyButton && (
              <div className="border-t border-stone-100 p-2 dark:border-stone-800">
                <Button
                  size="xs"
                  fullWidth
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApply();
                  }}
                >
                  Apply
                </Button>
              </div>
            )}
          </div>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
}
